import { CodeLanguages } from 'js/Constants';

export const nexlaFnDefinitions = [
  {
    action: 'stringHash',
    label: 'nex_stringHash',
    detail: 'Encrypt a string value',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("stringHash", hash_method, stringToHash)</b>
      <p></p>
      <p>Returns encrypted value of the input. Use this transform for automatically encrypting any string value, for ex encrypting PII attributes.</p>
      <p>
      <b><em>@param</em> hash_method</b>:  Hash encryption method. One of <b>md5</b> or <b>sha256</b>
      <br/>
     <b><em>@param</em> stringToHash</b>: The input string or attribute of which to return the encrypted value.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("stringHash", "sha256" , "test")  \u27a0 "9F86D081884C7D659A2FEAA0C55AD015A3BF4F1B2B0B822CD15D6C15B0F00A08"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("stringHash", ${1:hash_method}, ${2:stringToHash})',
  },
  {
    action: 'integerHash',
    label: 'nex_integerHash',
    detail: 'Encrypt an integer value',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("integerHash", hash_method, integerToHash)</b>
      <p></p>
      <p>
      Returns encrypted value of the input. Use this transform for automatically encrypting any integer value, for ex encrypting PII attributes.
      </p>
      <p>
      <b><em>@param</em> hash_method</b>:  Hash encryption method. One of __md5__ or __sha256__
      <br/>
      <b><em>@param</em> integerToHash</b>: The input integer or attribute of which to return the encrypted value.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("integerHash", "sha256" , 123)  \u27a0 1503946150
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("integerHash", ${1:hash_method}, ${2:integerToHash})',
  },
  {
    action: 'toMapValue2',
    label: 'nex_toMapValue',
    detail: 'Fetch column value from  Nexla lookup based on primary key match',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("toMapValue2", lookupId, lookupOnKey, lookupKeyToFetch)</b>
      <p></p>
      <p>
        Finds Lookup row  based on match of input attribute value with the primary key values of the specified Nexla lookup and returns the value of the Lookup Secondary key column. Secondary key is only required if the Lookup has more than one secondary key columns.
      </p>
      <p>
      <b><em>@param</em> lookupId</b>: Nexla Static or Dynamic lookup to fetch entries from.
      <br/>
      <b><em>@param</em> lookupOnKey</b>: Input attribute or value for filtering lookup entries. The filter is applied based on exact match of primary key with this value.
      <br/>
      <b><em>@param</em> lookupKeyToFetch</b>: The secondary key column from the lookup whose value from the matching row will be returned.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("getMap", 1200, "1", "code) \u27a0 "One" if lookup 1200 has a row with primary key value "1" and corresponding "code" value "One"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("toMapValue2", ${1:lookupId}, ${2:lookupOnKey}, ${3:lookupKeyToFetch})',
  },
  {
    action: 'getMap',
    label: 'nex_getMapEntries',
    detail: 'Fetch entire row from Nexla lookup based on primary key match',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("getMap", lookupId, lookupOnKey)</b>
      <p></p>
      <p>
        Fetch entire object from a lookup table.  Lookup entries are filtered based on exact match of input with lookup primary key. Returned value is a JSON object of <key>:<value> pairs that match <column-name>:<row-value> for the matching row.
      </p>
      <p>
      <b><em>@param</em> lookupId</b>: Nexla Static or Dynamic lookup to fetch entries from.
      <br/>
      <b><em>@param</em> lookupOnKey</b>: Input attribute or value for filtering lookup entries. The filter is applied based on exact match of primary key with this value.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("getMap", 1200, "1") \u27a0 {"id":1,"code":"One", "codeLower":"one"}
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("getMap", ${1:lookupId}, ${2:lookupOnKey})',
  },
  {
    action: 'epochFromString',
    label: 'nex_epochFromString',
    detail: 'Convert datetime string to epoch',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("epochFromString", inputString)</b>
      <p></p>
      <p>
        Converts a valid ISO8601 formatted string timestamp to the corresponding unix epoch time.
      </p>
      <p>
      <b><em>@param</em> inputString</b>: ISO8601 formatted input string to be converted to Unix epoch time.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("epochFromString", "2020-11-10T07:44:25Z") \u27a0 1604994265000
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("epochFromString", ${1:inputString})',
  },
  {
    action: 'extractDate',
    label: 'Extract parts from a datetime string',
    detail: 'nexla_fn.call("extractDate", toFormat, inputString);',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("extractDate", toFormat, inputString)</b>
      <p></p>
      <p>
        Converts a valid datetime string from one timezone to another timezone. Returns an ISO8601 format date-time string.
      </p>
      <p>
      <b><em>@param</em> toFormat</b>: Desired format of output that includes the extracted date-time parts of input string. Examples: __MM/dd__, __yy__, __yyyy__, __MM__, __MMM__, __MMMM__, __hh__, __hh:mm__, __HH__, __HH:mm__, __DD__
      <br/>
      <b><em>@param</em> inputString</b>: String value that needs to be parsed. Note that input attribute must be a string of valid date time format, example: 2018-09-23
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("extractDate", "dd-MMM-yy", "09/28/2017 11:43:00") \u27a0 "28-Sep-17"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("extractDate", ${1:toFormat}, ${2:inputString})',
  },
  {
    action: 'convertTimeZone',
    label: 'nex_convertTimeZone',
    detail: 'Convert a datetime string from one timezone to another',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("convertTimeZone", fromZone, toZone, timeToConvert)</b>
      <p></p>
      <p>
        Converts a valid datetime string from one timezone to another timezone. Returns an ISO8601 format date-time string.
      </p>
      <p>
      <b><em>@param</em> fromZone</b>: Time Zone to convert from.
      <br/>
      <b><em>@param</em> toZone</b>: Timezone to convert to.
      <br/>
      <b><em>@param</em> timeToConvert</b>: Date time string that needs to be converted to different time zone.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("convertTimeZone", "GMT", "America/New_York","2020/11/09") \u27a0 "2020-11-08T19:00:00-05:00"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("convertTimeZone", ${1:fromZone}, ${2:toZone}, ${3:timeToConvert})',
  },
  {
    action: 'timestampDifference',
    label: 'nex_timestampDifference',
    detail: 'Get the difference between two timestamps',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("timestampDifference", differenceUnit, subtractTs, fromTs)</b>
      <p></p>
      <p>
        Returns the absolute difference of two timestamps in one of the supported ISO8601 units of time. Note that this function returns the absolute difference between the two timestamps.
      </p>
      <p>
      <b><em>@param</em> differenceUnit</b>: Time unit for computing difference. Choose one of __sss__ (for milliseconds), __S__ (for seconds), __M__ (for minutes), __H__ (for hours), __D__ (for days), or __W__ (for weeks)
      <br/>
      <b><em>@param</em> subtractTs</b>: The input integer or attribute of which to return the encrypted value.
      <br/>
      <b><em>@param</em> fromTs</b>: Second timestamp to be used for computing the difference of timestamps.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("timestampDifference", "m", "2016-03-22T22:54:14Z","2016-03-22T22:44:14Z") \u27a0 10
      </p>
      </div>`,
    },
    snippet:
      'nexla_fn.call("timestampDifference", ${1:differenceUnit}, ${2:subtractTs}, ${3:fromTs})',
  },
  {
    action: 'iso8601FromEpoch',
    label: 'nex_iso8601FromEpoch',
    detail: 'Convert time (epoch) to ISO8601 format',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("iso8601FromEpoch", unixEpoch)</b>
      <p></p>
      <p>
        Converts a valid unix epoch time to the corresponding ISO8601 formatted string timestamp.
      </p>
      <p>
      <b><em>@param</em> unixEpoch</b>: Time (Unix Epoch)  to convert into ISO8601 format.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("iso8601FromEpoch", 1604994265) \u27a0 "2020-11-10T07:44:25Z"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("iso8601FromEpoch", ${1:unixEpoch})',
  },
  {
    action: 'iso8601FromString',
    label: 'nex_iso8601FromString',
    detail: 'Convert time (string) to ISO8601 format',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("iso8601FromString", timeString)</b>
      <p></p>
      <p>
        Converts a valid date-time string to the corresponding ISO8601 formatted string timestamp.
      </p>
      <p>
      <b><em>@param</em> timeString</b>: Time (String) to convert into ISO8601 format. This should comply with some valid date-time format.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("iso8601FromString","25/02/2017") \u27a0 "2017-02-25T00:00:00"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("iso8601FromString", ${1:timeString})',
  },
  {
    action: 'min',
    label: 'nex_min',
    detail: 'Get minimum value of elements in integer array',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("min", inputArray)</b>
      <p></p>
      <p>
        Returns the minimum value of elements in an array. Note that this function does not compute minimum across records.
      </p>
      <p>
      <b><em>@param</em> inputArray</b>: The array attribute to extract the minimum value from.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("min", [2,4,6]) \u27a0 2
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("min", ${1:inputArray})',
  },
  {
    action: 'max',
    label: 'nex_max',
    detail: 'Get maximum value of elements in integer array',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("max", inputArray)</b>
      <p></p>
      <p>
        Returns the maximum value of elements in an array. Note that this function does not compute maximum across records. Interprets string as a number.
      </p>
      <p>
      <b><em>@param</em> inputArray</b>: The array attribute to extract the maxium value from.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("max", [2,4,6]) \u27a0 6
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("max", ${1:inputArray})',
  },
  {
    action: 'avg',
    label: 'nex_avg',
    detail: 'Get average value of elements in integer array',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("avg", inputArray)</b>
      <p></p>
      <p>
        Returns the average value of elements in an array. Note that this function does not average across records. Interprets string as a number.
      </p>
      <p>
      <b><em>@param</em> inputArray</b>: The array attribute to extract the average of.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("avg", [2,4,6]) \u27a0 8
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("avg", ${1:inputArray})',
  },
  {
    action: 'bucket',
    label: 'nex_bucket',
    detail: 'Segment data values into equal sized bins.',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("bucket", numberOfBuckets, input)</b>
      <p></p>
      <p>
        Segment data values into equal sized bins. Given a number, returns the integer bin into which the number would fall. Similarly, for an array of numbers, returns an array of bins where each element of array would fall. Interprets string as a number.
      </p>
      <p>
      <b><em>@param</em> numberOfBuckets</b>: Size of  bucket or segment. The default bucket size is 10.
      <br/>
      <b><em>@param</em> input</b>: The number or array of numbers that needs to be segment into bins. The bin/segment value is equal to the lowest number representing start of the bin.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("bucket", 5, [11, 21, 99]) \u27a0 [10, 20, 95 ]
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("bucket", ${1:numberOfBuckets}, ${2:input})',
  },
  {
    action: 'join',
    label: 'nex_join',
    detail: 'Join array of strings with delimiter',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("join", delimiter, arrayOfStrings)</b>
      <p></p>
      <p>
        Join all elements of an array into a string. There is no separator applied between elements in this functions.
      </p>
      <p>
      <b><em>@param</em> delimiter</b>: The character or string separtor for joining array elements.
      <br/>
      <b><em>@param</em> arrayOfStrings</b>: Input array that needs to be converted to a string.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("join", " and ", ["First","Second"]) \u27a0 "First and Second"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("join", ${1:delimiter}, ${2:arrayOfStrings})',
  },
  {
    action: 'toStringWithLeadingZeroes',
    label: 'Convert integer to string of min length',
    detail: 'nexla_fn.call("toStringWithLeadingZeroes", lengthOfOutput, integerToConvert);',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("toStringWithLeadingZeroes", lengthOfOutput, integerToConvert)</b>
      <p></p>
      <p>
        Converts and integer to a string of at least a minimum length. If the integer is smaller than desired input length the string is padded with required number of 0s.
      </p>
      <p>
      <b><em>@param</em> lengthOfOutput</b>: Minumum desired length of output string.
      <br/>
      <b><em>@param</em> integerToConvert</b>: Input number to be converted.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("toStringWithLeadingZeroes", 5, 123) \u27a0 "00123"
      </p>
      </div>`,
    },
    snippet:
      'nexla_fn.call("toStringWithLeadingZeroes", ${1:lengthOfOutput}, ${2:integerToConvert})',
  },
  {
    action: 'GeoDetection',
    label: 'nex_geoDetection',
    detail: 'Extract location info from IP Address',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("GeoDetection", infoToExtract, fromIPAddress)</b>
      <p></p>
      <p>
        Extracts location information from a valid IP Address string.
      </p>
      <p>
      <b><em>@param</em> infoToExtract</b>:  The location information to extract. One of __city__, __continent__, __country__, __country_code__, __dma__, __latitude__, __longitude__, __postal__, __region__, __region_code__ or __timezone__.
      <br/>
      <b><em>@param</em> fromIPAddress</b>: Valid IP Address string to be parsed.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("GeoDetection", "country_code", "73.223.52.74") \u27a0 "US"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("GeoDetection", ${1:infoToExtract}, ${2:fromIPAddress})',
  },
  {
    action: 'UserAgentDetection',
    label: 'Extract device info from User Agent',
    detail: 'nexla_fn.call("UserAgentDetection", infoToExtract, fromUserAgent)',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("UserAgentDetection", infoToExtract, fromUserAgent)</b>;
      <p></p>
      <p>
        Extracts device information from a valid User Agent string.
      </p>
      <p>
      <b><em>@param</em> infoToExtract</b>:  The device information to extract. One of __browser__, __browser_version__, __device_make__, __device_type__ or __operating_system__
      <br/>
      <b><em>@param</em> fromUserAgent</b>: Valid User Agent string to be parsed.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("UserAgentDetection", "device_make", "Mozilla/5.0 (iPhone; CPU iPhone OS 13_5_1 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/13.1.1 Mobile/15E148 Safari/604.1") \u27a0 "Apple Inc."
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("UserAgentDetection", ${1:infoToExtract}, ${2:fromUserAgent})',
  },
  {
    action: 'VINDetection',
    label: 'nex_vinDetection',
    detail: 'Extract info from vehicle VIN',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("VINDetection", infoToExtract, fromVIN)</b>
      <p></p>
      <p>
        Extracts vehicle information from a valid VIN string.
      </p>
      <p>
      <b><em>@param</em> infoToExtract</b>:  The vehicle information to extract. One of __country__, __manufacturer__, or __year__
      <br/>
      <b><em>@param</em> fromVIN</b>: Valid VIN string to be parsed.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("VINDetection", "year", "1GNDM19X35B110457") \u27a0 "2005"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("VINDetection", ${1:infoToExtract}, ${2:fromVIN})',
  },
  {
    action: 'oneHotEncoding',
    label: 'nex_oneHotEncoding',
    detail: 'One hot encode an attribute',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("oneHotEncoding", lookupId, attrToEncode)</b>
      <p></p>
      <p>
      One hot encode an attribute into an object. One hot encoding is a process by which categorical variables are converted into a form that could be provided to ML algorithms to do a better job in prediction. This transform takes a Nexla Lookup and an attribute as the input and returns an object. The returned object has the same keys as the Lookup, the key matching the input attribute has value of 1 and all other keys have value of 0.
      </p>
      <p>
      <b><em>@param</em> lookupId</b>: Nexla lookup to perform encoding on.
      <br/>
      <b><em>@param</em> attrToEncode</b>: Attribute that if present as key in lookup will be encoded to 1.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("integerHash", 1234 , <attr>)  \u27a0 object with one key <attr> and value: 1, and all other columns from 1234 as keys with value 0
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("oneHotEncoding", ${1:lookupId}, ${2:attrToEncode})',
  },
  {
    action: 'grok',
    label: 'nex_grok',
    detail: 'Parse log lines by grok pattern',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("grok", grokPattern, inputString)</b>
      <p></p>
      <p>
      Parse unstructured log data into an object of structured fields using a grok pattern. Returns an object with predefined keys for that log format if text can be parsed, and an empty object if the text cannot be parsed. Select a predefined pattern or create your own valid grok pattern depending on the log format.
      </p>
      <p>
      <b><em>@param</em> grokPattern</b>: Grok Pattern to extract. Some example log patterns are: <b>%{SYSLOGBASE}</b> - Grok pattern for syslog, <b>%{COMBINEDAPACHELOG}</b> - Grok pattern for apache log, <b>%{NAGIOSLOGLINE}</b> - Grok pattern for Nagios log, <b>%{POSTGRESQL}</b> - Grok pattern for postgres server log, <b>%{REDISLOG}</b> - Grok pattern for redis server log, <b>%{JAVASTACKTRACEPART}</b> - Grok pattern for java stack trace.
      <br/>
      <b><em>@param</em> inputString</b>: The input string that should be parsed. Usually this should be text streamed from a standard log file like server logs.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("grok", "%{SYSLOGBASE}", "Jan  1 06:25:43 mailserver14 postfix/cleanup[21403]: BEF25A72965: message-id=<20130101142543.5828399CCAF@mailserver14.example.com>") \u27a0 \n{\n"MONTH": "Jan",\n"SYSLOGBASE": "Jan  1 06:25:43 mailserver14 postfix/cleanup[21403]:",\n"SYSLOGPROG": "postfix/cleanup[21403]",\n"HOUR": "06",\n"IPV6": null,\n"SYSLOGFACILITY": null,\n"IP": null,\n"IPV4": null,\n"TIME": "06:25:43",\n"pid": "21403",\n"program": "postfix/cleanup",\n"priority": null,\n"logsource": "mailserver14",\n"IPORHOST": "mailserver14",\n"HOSTNAME": "mailserver14",\n"MINUTE": "25",\n"SECOND": "43",\n"facility": null,\n"MONTHDAY": "1",\n"timestamp": "Jan  1 06:25:43"}
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("grok", ${1:grokPattern}, ${2:inputString})',
  },
  {
    action: 'regexFind',
    label: 'nex_regexFind',
    detail: 'Extract substrings matching pattern',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("regexFind", regexPatternBase64, inputString)</b>
      <p></p>
      <p>
      Returns an array of all matches of the regular expression in the input string.
      </p>
      <p>
      <b><em>@param</em> regexPatternBase64</b>: Base 64 encoded regular expression pattern to test the input string against.
      <br/>
      <b><em>@param</em> inputString</b>: Input string or text to test against the regular expression pattern.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("regexMatch", "W2EtekEtWl1bMS05XQ==", "abcd1234e5")  \u27a0 ["d1","e5"]
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("regexFind",${1:regexPatternBase64},${2:inputString})',
  },
  {
    action: 'regexMatch',
    label: 'nex_regexMatch',
    detail: 'Check if string matches pattern',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("regexMatch", regexPatternBase64, inputString)</b>
      <p></p>
      <p>
      Returns boolean true or false depending on whether the input string matches provided regular expression or not.
      </p>
      <p>
      <b><em>@param</em> regexPatternBase64</b>: Base 64 encoded regular expression pattern to test the input string against.
      <br/>
      <b><em>@param</em> inputString</b>: Input string or text to test against the regular expression pattern.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("regexMatch", "XihbYS16QS1aMC05X1wtXC5dKylAKFthLXpBLVowLTlfXC1cLl0rKVwuKFthLXpBLVpdezIsNX0pJA==", "test@acme.com")  \u27a0 true
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("regexMatch",${1:regexPatternBase64},${2:inputString})',
  },
  {
    action: 'toJsonString',
    label: 'nex_toJsonString',
    detail: 'Convert JSON object to JSON string',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("toJsonString", objectToStringify)</b>
      <p></p>
      <p>
      Converts a JSON Object/Array to a JSON String. This is useful for writing nested objects to database columns or lookup tables. Note that if the input is already a string it will be written as is.
      </p>
      <p>
      <b><em>@param</em> objectToStringify</b>:  The input object/attribute that needs to be flattened into a JSON string. This is best suited for cases where input attribute is a valid nested object or array of objects.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("toJsonString", {"one": 1, "two":2 })  \u27a0 "{\\"one\\":1,\\"two\\":2}"
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("toJsonString", ${1:objectToStringify})',
  },
  {
    action: 'getCredential',
    label: 'nex_getCredential',
    detail: 'Retrieve credential by ID',
    supportedLanguages: [CodeLanguages.JAVASCRIPT, CodeLanguages.PYTHON],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>nexla_fn.call("getCredential", id)</b>
      <p></p>
      <p>
        Retrieves a Nexla credential by its ID. Use this in custom transform code to access credential values (e.g. API keys, tokens) stored in the organization's credentials.
      </p>
      <p>
      <b><em>@param</em> id</b>: The credential ID to fetch.
      <br/>
      <br/>
      <b><em>@example:</em></b> nexla_fn.call("getCredential", 1234) \u27a0 credential object/value for credential 1234
      </p>
      </div>`,
    },
    snippet: 'nexla_fn.call("getCredential", ${1:id})',
  },
  // Start of Python3 definitions
  {
    action: 'lookup',
    label: 'nexla.lookup',
    detail: 'Fetch entire row from Nexla lookup based on primary key match',
    supportedLanguages: [CodeLanguages.PYTHON3],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>await nexla.lookup(lookup_id=lookupId, key=lookupOnKey)</b>
      <p></p>
      <p>
        Fetch entire object from a lookup table.  Lookup entries are filtered based on exact match of input with lookup primary key. Returned value is a JSON object of <key>:<value> pairs that match <column-name>:<row-value> for the matching row.
      </p>
      <p>
      <b><em>@param</em> lookupId</b>: Nexla Static or Dynamic lookup to fetch entries from.
      <br/>
      <b><em>@param</em> lookupOnKey</b>: Input attribute or value for filtering lookup entries. The filter is applied based on exact match of primary key with this value.
      <br/>
      <br/>
      <b><em>@example:</em></b> await nexla.lookup(lookup_id=1200, key="1") \u27a0 {"id":1,"code":"One", "codeLower":"one"}
      </p>
      </div>`,
    },
    snippet: 'await nexla.lookup(lookup_id=${1:lookupId}, key=${2:lookupOnKey})',
  },
  {
    action: 'get_nexset_data',
    label: 'nexla.get_nexset_data',
    detail: 'Fetch data from a specified Nexset using the Nexla Data Feed API',
    supportedLanguages: [CodeLanguages.PYTHON3],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>await nexla.get_nexset_data(id=datasetId, api_key=apiKey, args, no_cache)</b>
      <p></p>
      <p>
        Retrieves data from a specified Nexset using the Nexla Data Feed API. Returns the dataset records that can be used for lookups, joins, or other data operations within the transform.
      </p>
      <p>
      <b><em>@param</em> id</b>: The Nexset ID from which to retrieve data .
      <br/>
      <b><em>@param</em> api_key</b>: Nexla API key for authentication.
      <br/>
      <b><em>@param</em> args</b>: Optional dict of arguments to use as initial data record for the transformation pipeline, can contain special configuration keys like CUSTOM_CREDENTIALS_ID for custom authentication or dataset-specific parameters (defaults to empty dict).
      <br/>
      <b><em>@param</em> no_cache</b>: Boolean flag to bypass caching and fetch fresh data (defaults to False).
      <br/>
      <br/>
      <b><em>@example:</em></b> await nexla.get_nexset_data(id=1234, api_key="your_api_key", args=[], no_cache=False)
      </p>
      </div>`,
    },
    snippet:
      'await nexla.get_nexset_data(id=${1:dataset_id}, api_key=${2:api_key}, args=${3:args}, no_cache=${4:no_cache})',
  },
  {
    action: 'transform_attribute',
    label: 'nexla.transform_attribute',
    detail:
      'Apply a custom transformation to a single attribute value using a specified code container ID',
    supportedLanguages: [CodeLanguages.PYTHON3],
    documentation: {
      supportHtml: true,
      value: `<div>
      <b>await nexla.transform_attribute(code_container_id, input, metadata, args=["arg1", "arg2"])</b>
      <p></p>
      <p>
       Applies a custom transformation to a single attribute value using a specified code container ID. Executes the transformation logic defined in the code container against the input value.
      </p>
      <p>
      <b><em>@param</em> code_container_id</b>: he ID of the code container containing the transformation logic to apply.
      <br/>
      <b><em>@param</em> input</b>: The attribute value to transform.
      <br/>
      <b><em>@param</em> metadata</b>: Optional dictionary containing metadata about the transformation context (defaults to None).
      <br/>
      <b><em>@param</em> args</b>: Optional list of additional arguments to pass to the transformation function (defaults to None).
      <br/>
      <br/>
      <b><em>@example:</em></b> await nexla.transform_attribute(code_container_id=5678, input="sample_value", metadata='{"field": "name"}, args=["arg1", "arg2"])
      </p>
      </div>`,
    },
    snippet:
      'await nexla.transform_attribute(code_container_id=${1:code_container_id}, input=${2:input}, metadata=${3:metadata}, args=${4:args})',
  },
];

const mapDefinitionsToTS = () => {
  const regExpCall = /nexla_fn.call\(/;
  const regExpArgs = /\$\{\d+:([^}]+)}/g;

  return nexlaFnDefinitions.map((def) => {
    return def.snippet
      .replace(regExpCall, 'function call(action: ')
      .replace(regExpArgs, '$1: any')
      .replace(';', ': void;');
  });
};

export const nexlaFnModuleUriStr = 'ts:filename/nexla_fn.d.ts';
export const nexlaFnModuleTS = /* language=typescript */ `
declare type Action = '${nexlaFnDefinitions.map((def) => def.action).join("' | '")}';
declare namespace nexla_fn {
  ${mapDefinitionsToTS().join('\n')}
  function call(action: Action, ...args: any[]): void;
}
`.trim();
